import React, { useRef } from 'react';
import { useDispatch } from 'react-redux';

// Constants
import ExternalLinksConstants from 'Shared/constants/ExternalLinks.constants';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';
import { useMembers, useToggle } from 'Shared/utils/hooks';
import { useSmac } from 'Shared/utils/hooks/use-smac';

// Types
import { WidthType } from 'Wpds/components/dropdown';
import { Member, RoleType, OrganizationRoleType } from 'Shared/types/Member.type';

// Actions
import { updateMemberAction } from 'Shared/actions/members';

// Components
import { VisuallyHidden } from 'Wpds/components/visually-hidden/VisuallyHidden';
import { WpBadge } from 'Wpds/components/badge';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import { WpCheckbox } from 'Wpds/components/checkbox';
import { WpForm, WpFormLayout } from 'Wpds/components/form';
import { WpLink } from 'Wpds/components/link';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpRadioGroup, WpRadioInput } from 'Wpds/components/radio';
import { WpText } from 'Wpds/components/text';
import WpAlert from 'Wpds/components/alert/WpAlert';
import WpDivider from 'Wpds/components/divider/WpDivider';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpUserInput from 'Wpds/components/user-input/WpUserInput';

// Styles
import './ManageMemberModal.scss';

type Props = {
  isOpen: boolean;
  toggleOpen: () => void;
  member: Member;
  handleRemoveUser?: Function;
  editRole?: boolean;
  editAdminAccess?: boolean;
  editTitle?: boolean;
  editDepartment?: boolean;
  editManager?: boolean;
};

const ManageMemberModal = ({
  isOpen,
  toggleOpen,
  member,
  handleRemoveUser,
  editRole = true,
  editAdminAccess = true,
  editTitle = true,
  editDepartment = true,
  editManager = true,
}: Props) => {
  const showRemoveUserOption = handleRemoveUser && !member?.piUserGuid;
  const { app } = useApp();
  const { hasClassicAccess, hasAccessToNonPerformModules } = useSmac();

  const [isSaving, toggleIsSaving] = useToggle(false);
  const submitButtonRef = useRef(null);
  const dispatch = useDispatch();
  const { members } = useMembers();
  const isOnlyOneAdmin = members.filter(member => member.organizationRole === OrganizationRoleType.admin).length === 1;
  const isThirdPartyUser = !member.primary;
  const baseUrl = app.piAppUrl;

  let url: URL = new URL(ExternalLinksConstants.contact_us);
  if (hasAccessToNonPerformModules) {
    url = hasClassicAccess
      ? new URL('/administration/usersmanagement', baseUrl)
      : new URL('/pi/admin/users/third-party-users', baseUrl);
  }

  if (!isOpen && !member) return null;

  const isAdmin = member.organizationRole === OrganizationRoleType.admin;
  const validManagers = members.filter(m => m.userId !== member.userId);

  const onSave = values => {
    toggleIsSaving();
    const newRole = values.adminAccess ? OrganizationRoleType.admin : OrganizationRoleType.regular_user;

    const payload = {
      id: member.memberId,
    };

    if (editTitle) payload['title'] = values.title;
    if (editDepartment) payload['department'] = values.department;
    if (editManager) payload['managerId'] = values.manager?.userId || values.manager || null;
    if (editRole) payload['role'] = values.role;
    if (editAdminAccess) payload['organizationRole'] = newRole;

    dispatch(updateMemberAction(payload)).then(() => {
      toggleIsSaving();
      toggleOpen();
    });
  };

  const handleSaveButtonClick = () => {
    submitButtonRef.current.click();
  };

  const handleRemoveUserClick = () => {
    handleRemoveUser(member);
  };

  const baseClassName = 'ManageMemberModal';
  return (
    <WpModal isOpen={isOpen} onToggle={toggleOpen} size="sm" dismissOnBackdrop={false}>
      <WpModalHeader>Edit Member</WpModalHeader>
      <WpModalBody>
        <WpForm onSubmit={onSave}>
          <h5 className={`${baseClassName}__name`}>{member.fullName}</h5>
          {isThirdPartyUser && (
            <span className={`${baseClassName}__wrapper_badge`}>
              <WpBadge color="gray" size="sm" variant="bold">
                Third Party User
              </WpBadge>
            </span>
          )}
          <p className={`${baseClassName}__email`}>{member.email}</p>

          {isThirdPartyUser && (
            <div className={`${baseClassName}__alert`}>
              <WpAlert type="info">
                <WpText noMargin>
                  <WpText noMargin bold tag="span">
                    Note:{' '}
                  </WpText>
                  <span>
                    A third party user is a special user type intended for users outside your organization (such as your
                    PI Consultant).
                  </span>
                  {hasAccessToNonPerformModules ? (
                    <span>
                      &nbsp; Manage third party access from the <WpLink href={url.href}>PI Administration</WpLink> page.
                    </span>
                  ) : (
                    <span>
                      &nbsp; To manage third party access, reach out to <WpLink href={url.href}>Support.</WpLink>
                    </span>
                  )}
                </WpText>
              </WpAlert>
            </div>
          )}

          <WpFormLayout>
            {editRole && (
              <WpFormLayout>
                <WpRadioGroup
                  disabled={isThirdPartyUser}
                  id="role"
                  label="Role"
                  direction="horizontal"
                  defaultValue={member.role}
                >
                  <WpRadioInput id={RoleType.manager} label="Manager" value={RoleType.manager} />
                  <WpRadioInput id={RoleType.full_member} label="Individual contributor" value={RoleType.full_member} />
                </WpRadioGroup>
              </WpFormLayout>
            )}

            {editAdminAccess && (
              <>
                <WpDivider padding={0} />
                <WpCheckbox
                  id="adminAccess"
                  label="Has admin access"
                  checked={isAdmin}
                  disabled={(isAdmin && isOnlyOneAdmin) || isThirdPartyUser}
                />
                {isAdmin && isOnlyOneAdmin && (
                  <WpAlert type="note" showIcon={false}>
                    Add another admin to the organization before removing admin access
                  </WpAlert>
                )}
              </>
            )}

            {(editTitle || editDepartment) && (
              <>
                <WpDivider padding={0} />
                <WpFormLayout direction="horizontal">
                  {editTitle && (
                    <WpTextField disabled={isThirdPartyUser} id="title" label="Title" value={member.title} />
                  )}
                  {editDepartment && (
                    <WpTextField
                      disabled={isThirdPartyUser}
                      id="department"
                      label="Department"
                      value={member.department}
                    />
                  )}
                </WpFormLayout>
              </>
            )}
            {editManager && (
              <WpFormLayout>
                <WpUserInput
                  id="manager"
                  label="Manager"
                  value={member.managerId}
                  users={validManagers}
                  optionsWidth={WidthType.target}
                  removeUserLabel="Unassign manager"
                  addRemoveUserOption
                  disabled={isThirdPartyUser}
                />
              </WpFormLayout>
            )}
          </WpFormLayout>
          <VisuallyHidden>
            <WpButton btnType="submit" ref={submitButtonRef}>
              submit
            </WpButton>
          </VisuallyHidden>
        </WpForm>
      </WpModalBody>
      <WpModalFooter>
        <div className={`${baseClassName}__footer`}>
          {showRemoveUserOption ? (
            <WpButton color="danger" variant="secondary" onClick={handleRemoveUserClick}>
              Remove
            </WpButton>
          ) : (
            // Empty div to maintain save/cancel button position as the
            // display is justify-content: space-between
            <div></div>
          )}
          <WpButtonGroup>
            <WpButton variant="tertiary" onClick={toggleOpen} disabled={isSaving}>
              Cancel
            </WpButton>
            <WpButton disabled={isThirdPartyUser} onClick={handleSaveButtonClick} loading={isSaving}>
              Save
            </WpButton>
          </WpButtonGroup>
        </div>
      </WpModalFooter>
    </WpModal>
  );
};

export default ManageMemberModal;
